import {
  AlreadyPrrocessIcon,
  Block,
  Button,
  CanceledIcon,
  Column,
  ConfirmModal,
  FlexContainer,
  InputSelect,
  InputTextArea,
  Modal,
  NotFoundIcon,
  SoundNotificationContext,
  Span,
  SuccessLineIcon,
  TableSimple,
  Timer,
  TriangleAlertIcon,
  useNotifications,
} from '@uni-space/uni-components';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getErrorMessageAsync } from '@shared/api';
import { cancelParcel, findParcelsByTrackNumbers } from '../api';
import {
  BULK_CANCEL_CONFIRM_DELAY_MS,
  BulkCancelRow,
  BulkCancelRowStatus,
  parseTrackNumbers,
  sortBulkCancelRows,
} from '../model/bulkCancel';
import { useCancellationReasons } from '../model/useCancellationReasons';
import './BulkCancelParcels.scss';

/**
 * Массового эндпоинта отмены нет — каждая посылка отменяется отдельным POST v1/parcels/{track}/cancel.
 * Держим несколько запросов в полёте, чтобы сотня посылок не отменялась минуту.
 */
const CANCEL_CONCURRENCY = 3;

interface ReasonState {
  value: string;
  error: boolean;
}

export default function BulkCancelParcels() {
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotifications();
  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);
  const reasons = useCancellationReasons();

  const [rawInput, setRawInput] = useState<string>('');
  const [rows, setRows] = useState<BulkCancelRow[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const [isReasonModalOpen, setIsReasonModalOpen] = useState<boolean>(false);
  const [reason, setReason] = useState<ReasonState>({ value: '', error: false });

  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [isConfirmDisabled, setIsConfirmDisabled] = useState<boolean>(true);

  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });

  const parsedCount = useMemo(() => parseTrackNumbers(rawInput).length, [rawInput]);

  const counts = useMemo(() => {
    const acc = { found: 0, alreadyCanceled: 0, notFound: 0, canceled: 0, error: 0 };
    for (const row of rows) {
      switch (row.status) {
        case BulkCancelRowStatus.Found:
          acc.found++;
          break;
        case BulkCancelRowStatus.AlreadyCanceled:
          acc.alreadyCanceled++;
          break;
        case BulkCancelRowStatus.NotFound:
          acc.notFound++;
          break;
        case BulkCancelRowStatus.Canceled:
          acc.canceled++;
          break;
        case BulkCancelRowStatus.Error:
          acc.error++;
          break;
      }
    }
    return acc;
  }, [rows]);

  const isBusy = isSearching || isCancelling;

  const updateRow = useCallback((trackNumber: string, patch: Partial<BulkCancelRow>) => {
    setRows((prev) => prev.map((row) => (row.trackNumber === trackNumber ? { ...row, ...patch } : row)));
  }, []);

  const handleSearch = async () => {
    const trackNumbers = parseTrackNumbers(rawInput);
    if (!trackNumbers.length) return;

    setIsSearching(true);
    try {
      const parcels = await findParcelsByTrackNumbers(trackNumbers);
      const byTrackNumber = new Map(parcels.map((parcel) => [parcel.trackNumber, parcel]));

      const nextRows: BulkCancelRow[] = trackNumbers.map((trackNumber) => {
        const parcel = byTrackNumber.get(trackNumber);
        const status = !parcel
          ? BulkCancelRowStatus.NotFound
          : parcel.isCanceled
            ? BulkCancelRowStatus.AlreadyCanceled
            : BulkCancelRowStatus.Found;
        return { id: trackNumber, trackNumber, status };
      });

      setRows(sortBulkCancelRows(nextRows));
    } catch (err) {
      onSoundError();
      addNotification({ type: 'error', message: await getErrorMessageAsync(err, t('bulkCancel.searchError')) });
    } finally {
      setIsSearching(false);
    }
  };

  const handleClear = () => {
    setRawInput('');
    setRows([]);
  };

  const openReasonModal = () => {
    setReason({ value: '', error: false });
    setIsReasonModalOpen(true);
  };

  const closeReasonModal = () => {
    if (isCancelling) return;
    setIsReasonModalOpen(false);
    setReason({ value: '', error: false });
  };

  const handleReasonSubmit = () => {
    if (!reason.value) {
      setReason((prev) => ({ ...prev, error: true }));
      return;
    }
    // Таймер стартует заново при каждом открытии: кнопка подтверждения заблокирована,
    // пока не истекут BULK_CANCEL_CONFIRM_DELAY_MS.
    setIsConfirmDisabled(true);
    setIsConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (isCancelling) return;
    setIsConfirmOpen(false);
  };

  const runBulkCancel = async () => {
    const reasonItem = reasons.find((item) => item.label === reason.value);
    const targets = rows.filter((row) => row.status === BulkCancelRowStatus.Found);
    if (!reasonItem || !targets.length) return;

    setIsCancelling(true);
    setProgress({ done: 0, total: targets.length });

    let okCount = 0;
    let failedCount = 0;
    const queue = [...targets];

    const worker = async () => {
      for (;;) {
        const next = queue.shift();
        if (!next) return;

        try {
          await cancelParcel(next.trackNumber, reasonItem.reasonId, reasonItem.label);
          okCount++;
          updateRow(next.trackNumber, { status: BulkCancelRowStatus.Canceled, errorMessage: undefined });
        } catch (err) {
          failedCount++;
          const errorMessage = await getErrorMessageAsync(err, t('cancellationError'));
          updateRow(next.trackNumber, { status: BulkCancelRowStatus.Error, errorMessage });
        } finally {
          setProgress((prev) => ({ ...prev, done: prev.done + 1 }));
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(CANCEL_CONCURRENCY, targets.length) }, worker));

    setIsCancelling(false);
    setIsConfirmOpen(false);
    setIsReasonModalOpen(false);
    setReason({ value: '', error: false });

    if (failedCount === 0) {
      onSoundSuccess();
      addNotification({ type: 'success', message: t('bulkCancel.result.success', { n: okCount }) });
    } else {
      onSoundError();
      addNotification({
        type: 'error',
        message: t('bulkCancel.result.partial', { ok: okCount, failed: failedCount }),
      });
    }
  };

  const renderStatus = useCallback(
    (row: BulkCancelRow) => {
      switch (row.status) {
        case BulkCancelRowStatus.Found:
          return { icon: <SuccessLineIcon color="green" size="s" />, label: t('bulkCancel.status.found') };
        case BulkCancelRowStatus.AlreadyCanceled:
          return {
            icon: <AlreadyPrrocessIcon color="#f57c00" size="s" />,
            label: t('bulkCancel.status.alreadyCanceled'),
          };
        case BulkCancelRowStatus.NotFound:
          return { icon: <NotFoundIcon color="#EF6D7C" size="s" />, label: t('bulkCancel.status.notFound') };
        case BulkCancelRowStatus.Canceled:
          return { icon: <CanceledIcon color="green" size="s" />, label: t('bulkCancel.status.canceled') };
        case BulkCancelRowStatus.Error:
          return { icon: <TriangleAlertIcon color="#EF6D7C" size="s" />, label: t('bulkCancel.status.error') };
      }
    },
    [t],
  );

  const columns: Column<BulkCancelRow>[] = useMemo(
    () => [
      {
        header: t('tableHeader.trackNumber'),
        accessor: 'trackNumber',
        textAlign: 'center',
        flex: 1,
        minWidth: '180px',
        renderCell: (row) => (
          <span
            className={
              row.status === BulkCancelRowStatus.NotFound || row.status === BulkCancelRowStatus.Error
                ? 'bcp-code bcp-code--danger'
                : 'bcp-code'
            }
          >
            {row.trackNumber}
          </span>
        ),
      },
      {
        header: t('tableHeader.status'),
        accessor: 'status',
        textAlign: 'center',
        flex: 1,
        minWidth: '160px',
        renderCell: (row) => {
          const { icon, label } = renderStatus(row);
          return (
            <div className="bcp-status">
              {icon}
              <div>{label}</div>
            </div>
          );
        },
      },
      {
        header: t('bulkCancel.table.comment'),
        accessor: 'errorMessage',
        textAlign: 'center',
        flex: 1.5,
        minWidth: '160px',
        renderCell: (row) =>
          row.errorMessage ? <span className="bcp-errorMessage">{row.errorMessage}</span> : '—',
      },
    ],
    [renderStatus, t],
  );

  const hasResults = rows.length > 0;

  return (
    <div className="bcp-page">
      <Modal isOpen={isReasonModalOpen} onClose={closeReasonModal}>
        <FlexContainer style={{ position: 'relative', minWidth: 320 }} direction="column" gap="16px">
          <Block textAlign="center">
            <Span fs={18}>{t('bulkCancel.reasonModal.title', { n: counts.found })}</Span>
          </Block>
          <Block>
            <InputSelect
              withOutSearch
              value={reason.value}
              onChange={(val) => setReason({ value: val, error: false })}
              id="bulk_cancel_reason"
              variant="primary"
              placeholder={t('reasonForReturn')}
              items={reasons}
              disabled={isCancelling}
            />
            <Block pt={8}>
              <Span fs={12} color={reason.error ? 'red' : 'rgba(0, 0, 0, 0.6)'}>
                {t('selectReasonForCancellation')}
              </Span>
            </Block>
          </Block>
          <FlexContainer justify="space-between">
            <Button onClick={closeReasonModal} width="100px" size="s" disabled={isCancelling}>
              {t('close')}
            </Button>
            <Button onClick={handleReasonSubmit} type="error" width="100px" size="s" disabled={isCancelling}>
              {t('cancel')}
            </Button>
          </FlexContainer>
        </FlexContainer>
      </Modal>

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={closeConfirm}
        onSubmit={runBulkCancel}
        isLoading={isCancelling}
        disabled={isConfirmDisabled || isCancelling}
        lang={i18n.language}
        desc={
          <FlexContainer direction="column" align="center" justify="center">
            <Block mb={8}>
              <Span>{t('bulkCancel.confirm.desc', { n: counts.found + counts.canceled + counts.error })}</Span>
            </Block>
            {isCancelling ? (
              <Span fw={600}>{t('bulkCancel.confirm.progress', { done: progress.done, total: progress.total })}</Span>
            ) : (
              isConfirmOpen && (
                <Timer onComplete={() => setIsConfirmDisabled(false)} durationMs={BULK_CANCEL_CONFIRM_DELAY_MS} />
              )
            )}
          </FlexContainer>
        }
      />

      <div className="bcp-card">
        <div className="bcp-cardHead">
          <div className="bcp-title">{t('bulkCancel.title')}</div>
          <span className="bcp-chip">{t('bulkCancel.parsedCount', { n: parsedCount })}</span>
        </div>

        <InputTextArea
          id="bulk_cancel_track_numbers"
          label={t('bulkCancel.inputLabel')}
          value={rawInput}
          rows={8}
          disabled={isBusy}
          spellCheck={false}
          autoComplete="off"
          onChange={(value) => setRawInput(value)}
        />
        <div className="bcp-hint">{t('bulkCancel.inputHint')}</div>

        <div className="bcp-actions">
          <Button onClick={handleSearch} disabled={parsedCount === 0 || isBusy} loading={isSearching} width="160px">
            {t('bulkCancel.search')}
          </Button>
          <Button onClick={handleClear} type="secondary" disabled={isBusy || (!rawInput && !hasResults)} width="160px">
            {t('bulkCancel.clear')}
          </Button>
          <Button
            onClick={openReasonModal}
            type="error"
            disabled={counts.found === 0 || isBusy}
            loading={isCancelling}
            width="240px"
          >
            {t('bulkCancel.cancelButton', { n: counts.found })}
          </Button>
        </div>
      </div>

      <div className="bcp-card">
        <div className="bcp-cardHead">
          <div className="bcp-title">{t('bulkCancel.resultsTitle')}</div>
          <div className="bcp-summary">
            <span className="bcp-chip bcp-chip--success">{t('bulkCancel.summary.found', { n: counts.found })}</span>
            <span className="bcp-chip bcp-chip--warning">
              {t('bulkCancel.summary.alreadyCanceled', { n: counts.alreadyCanceled })}
            </span>
            <span className="bcp-chip bcp-chip--danger">{t('bulkCancel.summary.notFound', { n: counts.notFound })}</span>
            {(counts.canceled > 0 || counts.error > 0) && (
              <>
                <span className="bcp-chip bcp-chip--success">
                  {t('bulkCancel.summary.canceled', { n: counts.canceled })}
                </span>
                <span className="bcp-chip bcp-chip--danger">{t('bulkCancel.summary.error', { n: counts.error })}</span>
              </>
            )}
          </div>
        </div>

        <TableSimple<BulkCancelRow>
          columns={columns}
          data={rows}
          enableColumnResizing={false}
          withOutPagination
          isLoading={isSearching}
          emptyPlaceholder={t('bulkCancel.table.empty')}
          mobileWidth={680}
          maxHeightTable="calc(100dvh - 520px)"
        />
      </div>
    </div>
  );
}
